import React, { useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { FaCopy, FaFileDownload, FaPrint } from "react-icons/fa";
import myConfig from "../../configs/config";
import {
  APP_TERMS_SECTIONS,
  PHONE_USE_CONTRACT_TEMPLATE,
} from "../../legal/appTerms";
import {
  copyOperatorTermsAcceptance,
  exportOperatorTermsAcceptance,
  formatOperatorTermsAcceptedAt,
  printOperatorTermsAcceptance,
} from "../../legal/printOperatorTerms";

function stop(event) {
  if (!event) return;
  event.preventDefault();
  event.stopPropagation();
}

function OperatorTermsModal({ operator, farmId, onClose, onClausesChanged }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [draft, setDraft] = useState("");
  const [clauses, setClauses] = useState(
    Array.isArray(operator.contract_clauses) ? operator.contract_clauses : []
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  if (!operator || typeof document === "undefined" || !document.body) {
    return null;
  }

  const details = {
    operatorName: operator.nome,
    farmName: operator.farm_name,
    acceptedAt: operator.app_terms_accepted_at,
    identification: operator.ident,
    contractClauses: clauses,
  };
  const when = formatOperatorTermsAcceptedAt(operator.app_terms_accepted_at);

  async function handleCopy(event) {
    stop(event);
    try {
      const ok = await copyOperatorTermsAcceptance(details);
      if (ok) {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      }
    } catch (_) {
      setCopied(false);
    }
  }

  async function handleSaveClause(event) {
    stop(event);
    const body = draft.trim();
    if (!body || saving) return;
    setSaving(true);
    setSaveError("");
    try {
      const response = await axios.post(
        `${myConfig.API_URL}/operator/${operator.id}/contract_clauses/${
          farmId ? `?farm_id=${farmId}` : ""
        }`,
        { body },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("client-token")}`,
          },
        }
      );
      const next = clauses.concat(response.data);
      setClauses(next);
      setDraft("");
      if (onClausesChanged) {
        onClausesChanged(operator.id, next);
      }
    } catch (_) {
      setSaveError(t("Não foi possível salvar a cláusula."));
    } finally {
      setSaving(false);
    }
  }

  return createPortal(
    <div
      className="operator-terms-modal"
      onMouseDown={stop}
      onClick={(event) => {
        stop(event);
        onClose(event);
      }}
    >
      <div
        className="operator-terms-modal__card"
        onMouseDown={stop}
        onClick={stop}
      >
        <div className="operator-terms-modal__header">
          <h3>{t("Termo de aceite")}</h3>
          <p>
            <strong>{operator.nome}</strong>
            {operator.ident ? ` · ${operator.ident}` : ""}
            {operator.farm_name ? ` · ${operator.farm_name}` : ""}
          </p>
          <p>
            {operator.app_terms_accepted
              ? t("Leu e aceitou os termos no app em {{when}}", {
                  when: when || "—",
                })
              : t("Ainda não aceitou os termos no app")}
          </p>
          <p className="operator-terms-modal__lock-note">
            {t(
              "O texto aceito no app não pode ser alterado. Só é possível acrescentar cláusulas abaixo."
            )}
          </p>
        </div>
        <div className="operator-terms-modal__body">
          {APP_TERMS_SECTIONS.map((section) => (
            <section key={section.title}>
              <h4>{section.title}</h4>
              {(section.paragraphs || []).map((text) => (
                <p key={text}>{text}</p>
              ))}
              {(section.bullets || []).length ? (
                <ul>
                  {(section.bullets || []).map((text) => (
                    <li key={text}>{text}</li>
                  ))}
                </ul>
              ) : null}
              {(section.after || []).map((text) => (
                <p key={text}>{text}</p>
              ))}
            </section>
          ))}

          <section className="operator-terms-modal__contract">
            <h4>{t("Contrato de uso")}</h4>
            {clauses.length ? (
              clauses.map((clause, index) => (
                <article
                  key={clause.id || index}
                  className="operator-terms-modal__clause"
                >
                  <small>
                    {t("Cláusula {{number}} — acrescentada em {{when}}", {
                      number: index + 1,
                      when:
                        formatOperatorTermsAcceptedAt(clause.created_at) || "—",
                    })}
                  </small>
                  <p>{clause.body}</p>
                </article>
              ))
            ) : (
              <p className="operator-terms-modal__empty">
                {t("Nenhuma cláusula extra foi acrescentada ainda.")}
              </p>
            )}
            <label htmlFor={`operator-clause-draft-${operator.id}`}>
              {t("Acrescentar cláusula")}
            </label>
            <textarea
              id={`operator-clause-draft-${operator.id}`}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              rows={6}
              placeholder={t(
                "Digite ou cole aqui a nova cláusula. O termo aceito no app permanece intacto."
              )}
            />
            <div className="operator-terms-modal__draft-actions">
              <button
                type="button"
                className="btn btn-default"
                onClick={(event) => {
                  stop(event);
                  setDraft(PHONE_USE_CONTRACT_TEMPLATE);
                }}
              >
                {t("Usar modelo de uso do celular")}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!draft.trim() || saving}
                onClick={handleSaveClause}
              >
                {saving ? t("Salvando...") : t("Salvar cláusula")}
              </button>
            </div>
            {saveError ? (
              <p className="operator-terms-modal__error">{saveError}</p>
            ) : null}
          </section>
        </div>
        <div className="operator-terms-modal__actions">
          <button
            type="button"
            className="btn btn-default"
            onClick={(event) => {
              stop(event);
              printOperatorTermsAcceptance(details);
            }}
          >
            <FaPrint aria-hidden="true" /> {t("Imprimir")}
          </button>
          <button
            type="button"
            className="btn btn-default"
            onClick={(event) => {
              stop(event);
              exportOperatorTermsAcceptance(details);
            }}
          >
            <FaFileDownload aria-hidden="true" /> {t("Exportar")}
          </button>
          <button
            type="button"
            className="btn btn-default"
            onClick={handleCopy}
          >
            <FaCopy aria-hidden="true" />{" "}
            {copied ? t("Copiado") : t("Copiar")}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={(event) => {
              stop(event);
              onClose(event);
            }}
          >
            {t("Fechar")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default OperatorTermsModal;
